/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../util/JumpToActionHelper',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../content/slotActions/api/SlotActionsProviderAPI',
	'../../content/dataPointActions/api/DataPointActionsProviderAPI',
	'../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI',
	'../../../widgets/livewidget/nls/StringResources'
], function(_, JumpToActionHelper, APIFactory, SlotActionsProviderAPI, DataPointActionsProviderAPI, ContentActionsProviderAPI, StringResources) {

	return class JumpToAction {
		constructor(options) {
			this._dashboard = options.features && options.features.API || options.dashboardAPI;
			this._icons = options.features.Icons || options.features['Dashboard.Icons'];
			if (options.content) {
				this._content = options.content;
				this._content.getFeature('SlotActions').registerProvider('jumpToAction', this.getAPI());
				this._content.getFeature('DataPointActions').registerProvider('jumpToAction', this.getAPI());
			} else {
				options.features && options.features.ContentActions.registerProvider('jumpToAction', this.getAPI());
			}
		}

		destroy () {
			this._dashboard = null;
			this._content = null;
		}

		getSlotActionList(dataSlotId, itemIndex, options = {}) {
			// Valid values will be any number or an array of 1 number
			if (Array.isArray(itemIndex) && itemIndex.length !== 1) {
				return [];
			}
			if (options.area === 'cell_edge_row' || options.area === 'cell_edge_column') {
				return [];
			}
			return this._getContribution();
		}

		getDataPointActionList(selections, actionOptions) {
			return this._supportDataPointAction(actionOptions) ? this._getContribution(selections, actionOptions) : [];
		}

		_supportDataPointAction(actionOptions) {
			return !actionOptions.noDrillthrough;
		}

		_supportAction(idList) {
			if (idList.length !== 1 || this._dashboard.getMode() === this._dashboard.MODES.EDIT_GROUP) {
				return false;
			}
			this._content = this._dashboard.getCanvas().getContent(idList[0]);
			return this._content && this._content.getType() === 'widget.live';
		}

		getContentActionList(idList) {
			if (this._supportAction(idList)) {
				this._content = this._dashboard.getCanvas().getContent(idList[0]);
				return this._getDrillThroughToolbarContribution();
			}
			return [];
		}

		_getTargetInfo(content, dataSourceId, selections) {
			//@todo also take into account net page context (next thing to do)
			const drillThroughModel = this._dashboard.getFeature('DrillThroughService').getDrillThroughModelApiSync();
			const entries = drillThroughModel.getDrillDefinitionEntries(content);

			return JumpToActionHelper.getJumpToTargets( {
				content: content,
				drillDefinitions: entries,
				selections: selections,
				dashboardApi: this._dashboard,
				sourceId: dataSourceId
			} );
		}

		_getContribution(selections) {
			let jumpToTargets = [];
			let toolbarContribution = [];

			const visualizationAPI = this._content.getFeature('Visualization');
			const dataSource = visualizationAPI.getDataSource();
			const dataSourceId = dataSource.getId();

			jumpToTargets = this._getTargetInfo(this._content, dataSourceId, selections);

			if (jumpToTargets.targets.length > 0) {
				const contribution = this._getContributionAction({
					oTargetsInfo: jumpToTargets,
					isDataPointSelection: true,
					sourceId: dataSourceId,
					selections: selections
				});

				toolbarContribution = [contribution];
			}
			return toolbarContribution;
		}

		_isAutoGrouped(visualizationAPI) {
			const dataItems = visualizationAPI.getSlots().getDataItemList();
			let bAutoGroup = false;
			dataItems.forEach((dataItem)=> {
				if (dataItem.getBinning()) {
					bAutoGroup = true;
				}
			});
			return bAutoGroup;
		}

		_getDrillThroughToolbarContribution() {
			const visualizationAPI = this._content.getFeature('Visualization');
			// Do not show drillthrough if autoGroup is enabled
			const visDefinitionType = visualizationAPI && visualizationAPI.getDefinition() && visualizationAPI.getDefinition().getType();
			if (visDefinitionType && !this._isAutoGrouped(visualizationAPI)) {
				const dataSource = visualizationAPI.getDataSource();
				if (!dataSource || !this._isInteractionEnabled('drillThrough')) {
					return [];
				}
				const dataSourceId = dataSource.getId();

				const oTargetsInfo = this._getTargetInfo(this._content, dataSourceId);

				const noTargets = oTargetsInfo.targets && !oTargetsInfo.targets.length;
				const { authoring } = this._dashboard.getFeature('DashboardState').getUiState();

				if (!authoring && noTargets) {
					return [];
				}

				return [this._getContributionAction({
					sourceId: dataSourceId,
					oTargetsInfo,
					isAuthoringMode: authoring
				})];
			}

			return [];
		}

		_getContributionAction(options = {}) {
			const oTargetsInfo = options.oTargetsInfo;
			const isAuthoringMode = !!options.isAuthoringMode;
			const isDataPointSelection = !!options.isDataPointSelection;
			const drillThroughIcon = this._icons.getIcon('visualizations-drillthrough_16');

			let oneTarget;
			if (isDataPointSelection) {
				oneTarget = oTargetsInfo.targets && oTargetsInfo.targets.length === 1;
			} else {
				oneTarget = isAuthoringMode ? false : oTargetsInfo.targets && oTargetsInfo.targets.length === 1;
			}

			let contribution = {
				name: 'jumpTo',
				label: StringResources.get('drillthrough_jumpToLabel'),
				icon: drillThroughIcon.id,
				viewOptions: {
					content: this._content,
					sourceId: options.sourceId,
					jumpToTargets: (content, dataSourceId, selections) => this._getTargetInfo(content, dataSourceId, selections),
					isDataPointSelection: isDataPointSelection,
					dashboardApi: this._dashboard,
					selections: options.selections
				}
			};

			if (oneTarget) {
				contribution = _.extend(contribution, {
					type: 'Button',
					actions: {
						apply: this._performDirectJumpTo.bind(this, oTargetsInfo)
					}
				});
			} else {
				contribution = _.extend(contribution, {
					type: 'NextView',
					viewModule: 'dashboard-analytics/drill-through/jumpTo/JumpToView'
				});
			}
			return contribution;
		}

		_performDirectJumpTo(targetsInfo) {
			if (targetsInfo && _.isArray(targetsInfo.targets) && !!targetsInfo.targets.length) {
				const id = targetsInfo.targets[0].getId();
				const target = targetsInfo.targetsMap[id];
				if (target) {
					target.jumpTo();
				}
			}
		}

		_isInteractionEnabled(name) {
			const config = this._dashboard.getAppConfig('interactions') || {};
			if (config.hasOwnProperty(name) && [false, 'false'].indexOf(config[name]) !== -1) {
				return false;
			}
			return true;
		}

		getAPI() {
			if (!this._api) {
				this._api = APIFactory.createAPI(this, [SlotActionsProviderAPI, ContentActionsProviderAPI, DataPointActionsProviderAPI]);
			}
			return this._api;
		}
	};
});
